import { colors } from "@indeed/ifl-tokens";
import { useCallback } from "react";
import { Platform } from "react-native";

import { Box } from "../../../../mads";
// Note: This file is a copy of the Chip component from the IFL library.
import { Button } from "./Button";
import { Label } from "./Label";

type ChipSize = "sm" | "md" | "lg";

const spacingY = {
  sm: 0,
  md: 1,
  lg: 3,
};

const spacingTopIOS = {
  sm: 0,
  md: 8,
  lg: 12,
};

const spacingX = {
  sm: 0,
  md: 8,
  lg: 12,
};

const labelSizes: { [key: string]: "sm" | "md" } = {
  sm: "sm",
  md: "md",
  lg: "md",
};

export interface ChipProps {
  /**
   * Styles the Chip to represent an invalid state.
   */
  invalid?: boolean;
  /**
   * Text label for the Chip.
   */
  label: string;
  /**
   * A unique identifier for the value that the option represents
   */
  value: string;
  /**
   * Size of the Chip. Use the values `sm`, `md`, or `lg`.
   */
  size?: ChipSize;
  /**
   * whether the chip is enabled or not
   */
  checked: boolean;
  /**
   * callback for when the chip is checked, pass the value supplied back as an argument,
   * not called if the chip is already checked
   */
  onSelect: (value: string) => void;
  /**
   * whether the element should be disabled
   */
  disabled?: boolean;
}

/**
 * @see https://experience.indeed.design/components/ids/chip
 *
 * For now only the default 'single' chip type is supported
 */
export function Chip({
  disabled = false,
  invalid = false,
  label,
  value,
  size = "md",
  checked,
  onSelect,
}: ChipProps) {
  const borderColor = invalid
    ? colors.border.status.critical.strong
    : colors.border.action["[base]"];
  const backgroundColor = checked
    ? colors.background.action.inverse["[base]"]
    : colors.background.action.weaker["[base]"];
  const color = checked ? colors.foreground.inverse["[base]"] : colors.foreground.strong;

  const onPress = useCallback(() => {
    if (checked) {
      return;
    }
    onSelect(value);
  }, [onSelect, value, checked]);

  return (
    <Button
      sx={{
        backgroundColor,
        margin: 0,
        overflow: "hidden",
        borderWidth: 1,
        borderStyle: "solid",
        borderRadius: 9999,
        borderColor,
        flexDirection: "row",
        alignItems: "center",
      }}
      disabled={disabled}
      onPress={onPress}
    >
      <Box
        sx={{
          paddingHorizontal: spacingX[size],
          paddingVertical: Platform.OS === "ios" ? spacingTopIOS[size] : spacingY[size],
        }}
      >
        <Label disabled={disabled} size={labelSizes[size]} fontWeight="normal" color={color}>
          {label}
        </Label>
      </Box>
    </Button>
  );
}
